import { TLocationAddress, TOfficeManager, TOffices } from "intake_shared/src/types";
import firebase from "firebase/compat/app";
import db from "../../utils/firestore";
import { Collection } from "intake_shared/src/enum";
import { arrayRemove, arrayUnion } from "firebase/firestore";

enum Location {
    MADISON = "Madison",
    UES = "Upper East Side",
    BROOKLYN = "Brooklyn",
    Remote = "Remote",
}

let officesCache: TOffices = {};
let officeManagerCache: TOfficeManager = {};
let locationAddressCache: TLocationAddress = {};

// getAllOffices, getOfficeManagers and getLocationAddress are all called at
// app start and each needs the same small collection. Share one in-flight read
// so startup costs one read per location document instead of three.
let inFlightLocationDocs: Promise<
    firebase.firestore.QueryDocumentSnapshot[]
> | null = null;
function readLocationDocs() {
    if (!inFlightLocationDocs) {
        inFlightLocationDocs = db
            .collection(Collection.LOCATIONS)
            .get()
            .then((snapshot) => snapshot.docs)
            .finally(() => {
                inFlightLocationDocs = null;
            });
    }
    return inFlightLocationDocs;
}

export const defaultOffices = {
    [Location.MADISON]: [
        "516",
        "502",
        "511",
        "510",
        "509",
        "517",
        "515",
        "514",
        "534(513)",
        "503",
        "504",
        "507",
        "518",
        "519",
        "506",
        "Add 1",
        "Add 2",
        "Add 3",
        "Add 4",
        "Add 5",
        "Add 6",
        "Add 7",
        "Add 8",
        "Add 9",
        "Add 10",
        "Add 11",
        "Add 12",
        "Add 13",
        "Add 14",
        "Add 15",
        "Add 16",
        "Add 17",
        "Add 18",
        "Add 19",
        "Add 20",
        "Add 21",
        "Add 22",
        "Add 23",
        "Add 24",
        "Add 25",
        "Add 26",
        "Add 27",
    ],
    [Location.UES]: ["12060"],
    [Location.BROOKLYN]: [
        "12060",
        "12061",
        "Add 1",
        "Add 2",
        "Add 3",
        "Add 4",
        "Add 5",
        "Add 6",
        "Add 7",
        "Add 8",
        "Add 9",
        "Add 10",
    ],
    // [Location.FD]: ['825', '824', '826', '827', '821', 'Add 1', 'Add 2', 'Add 3', 'Add 4', 'Add 5', 'Add 6', 'Add 7'],
    [Location.Remote]: [
        "Remote",
        "Remote 2",
        "Remote 3",
        "Remote 4",
        "Remote 5",
        "Remote 6",
        "Remote 7",
        "Remote 8",
        "Remote 9",
        "Remote 10",
    ],
};

export const defaultOfficeManager = {
    [Location.MADISON]: [],
};

export const defaultLocationAddress = {
    [Location.MADISON]: "136 Madison Ave. New York, NY",
};

export async function addLocation(
    locationName: string,
    locationAddress: string,
    officeManager?: string[]
) {
    const locationData: {
        offices: never[];
        office_managers?: string[];
        address: string;
    } = {
        offices: [],
        address: "",
    };

    if (officeManager) {
        locationData.office_managers = officeManager;
    }

    if (locationAddress) {
        locationData.address = locationAddress;
    }

    await db.collection(Collection.LOCATIONS).doc(locationName).set(locationData);
}

export async function addOffice(locationName: string, officeName: string) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(locationName);
    await locationRef.update({
        offices: arrayUnion(officeName),
    });
}

export async function addOfficeManager(
    locationName: string,
    newOfficeManagers: string[]
) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(locationName);
    await locationRef.update({
        office_managers: arrayUnion(...newOfficeManagers),
    });
}

export async function removeOfficeManager(
    locationName: string,
    officeManager: string
) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(locationName);
    await locationRef.update({
        office_managers: arrayRemove(officeManager),
    });
}

export async function removeOffice(locationName: string, officeName: string) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(locationName);
    await locationRef.update({
        offices: arrayRemove(officeName),
    });
}

export async function removeLocation(locationName: string) {
    await db.collection(Collection.LOCATIONS).doc(locationName).delete();
    // TODO: Check if they have been assigned to office windows before deleting them.
}

export async function updateOfficesCache() {
    const locationDocs = await readLocationDocs();
    if (locationDocs.length > 0) {
        officesCache = locationDocs.reduce((prev, curr) => {
            prev[curr.id] = curr.data().offices;
            return prev;
        }, {} as { [key: string]: string[] });
        return officesCache;
    }
}

export async function editOfficeName(
    location: string,
    officeId: string,
    newOfficeName: string
) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(location);
    const locationData = await locationRef.get();
    if (locationData.exists) {
        const locationDataSnapshot = locationData.data();
        if (locationDataSnapshot && locationDataSnapshot.offices) {
            if (locationDataSnapshot.offices.includes(officeId)) {
                // Create a copy of the offices array
                const updatedOffices = [...locationDataSnapshot.offices];
                // Find the index of the office to update
                const index = updatedOffices.indexOf(officeId);
                if (index !== -1) {
                    // Update the office name in the copy
                    updatedOffices[index] = newOfficeName;
                    // Update Firestore with the updated array
                    await locationRef.update({ offices: updatedOffices });
                }
            }
        }
    }
}

export async function updateLocationAddress(location: string, address: string) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(location);
    await locationRef.update({ address: address });
}

export async function deleteOffice(location: string, officeId: string) {
    const locationRef = db.collection(Collection.LOCATIONS).doc(location);
    const locationData = await locationRef.get();

    if (locationData.exists) {
        const locationDataSnapshot = locationData.data();
        if (locationDataSnapshot && locationDataSnapshot.offices) {
            if (locationDataSnapshot.offices.includes(officeId)) {
                // Create a copy of the offices array
                const updatedOffices = [...locationDataSnapshot.offices];
                // Find the index of the office to delete
                const index = updatedOffices.indexOf(officeId);
                if (index !== -1) {
                    // Remove the office from the copy
                    updatedOffices.splice(index, 1);
                    // Update Firestore with the updated array
                    await locationRef.update({ offices: updatedOffices });
                }
            }
        }
    }
}

export async function updateOfficeMangerCache() {
    const locationDocs = await readLocationDocs();
    if (locationDocs.length > 0) {
        officeManagerCache = locationDocs.reduce((prev, curr) => {
            prev[curr.id] = curr.data().office_managers;
            return prev;
        }, {} as { [key: string]: string[] });

        // This is to sort the office managers in descending order by their index in the array
        Object.values(officeManagerCache).forEach((managers) => {
            managers?.sort((a, b) => {
                const indexA = managers.indexOf(a);
                const indexB = managers.indexOf(b);
                return indexB - indexA;
            });
        });

        return officeManagerCache;
    }
}

export async function updateLocationAddressCache() {
    const locationDocs = await readLocationDocs();
    if (locationDocs.length > 0) {
        locationAddressCache = locationDocs.reduce((prev, curr) => {
            prev[curr.id] = curr.data().address;
            return prev;
        }, {} as { [key: string]: string });
    }
    return locationAddressCache;
}
export async function getAllOffices(): Promise<TOffices> {
    if (Object.keys(officesCache).length > 0) {
        return officesCache;
    }
    let offices = await updateOfficesCache();
    return offices || defaultOffices;
}

export async function getOfficeManagers(): Promise<TOfficeManager> {
    if (Object.keys(officeManagerCache).length > 0) {
        return officeManagerCache;
    }
    let officeManager = await updateOfficeMangerCache();
    return officeManager || defaultOfficeManager;
}

export async function getLocationAddress(): Promise<TLocationAddress> {
    if (Object.keys(locationAddressCache).length > 0) {
        return locationAddressCache;
    }
    await updateLocationAddressCache();
    return Object.keys(locationAddressCache).length > 0
        ? locationAddressCache
        : defaultLocationAddress;
}
